import { FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Category, QueryDataSource, QueryItem } from '../models/DataSource';
import { Storefront } from '../models/StorefrontResponse';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { QueryFilter } from '../models/Blueprint';
import QueryFieldCustom from './QueryFieldCustom';

interface QueryFieldProps {
    dataSource: QueryDataSource | undefined;
    queryItem: QueryItem;
    whitelistedQueryFields: string[];
    storefront: Storefront
    removeField: (fieldID: string) => void;
    updateFilter:(filter: QueryFilter, queryItemID: string) => void;
}

const QueryFieldForm = (props: QueryFieldProps) => {
    const [fieldName, setFieldName] = useState("");
    const [fieldValue, setFieldValue] = useState<string[]>([]); 
    const [fieldValueStartRange, setFieldValueStartRange] = useState<string>("");
    const [fieldValueEndRange, setFieldValueEndRange] = useState<string>("");
    const [fieldValueMultiple, setFieldValueMultiple] = useState<string>(""); // For fields that don't have a values object
    const [fieldCondition, setFieldCondition] = useState("and");
    const [fieldOmit, setFieldOmit] = useState("false");
    const [categories, setCategories] = useState<Category[]>([]);

    useEffect(() => {
        setFieldName(props.queryItem.field.key);
        setFieldValue(props.queryItem.filter.values || []);
        setFieldCondition(props.queryItem.filter.condition || "and");
        setFieldOmit(props.queryItem.filter.omit?.toString() || "false");

        if (isRangeField(props.queryItem.field.key)) {
            if (props.queryItem.filter.values.length === 1) {
                const rangeParts = props.queryItem.filter.values[0].split("-");

                if (rangeParts.length !== 2) {
                    return;
                }
                setFieldValueStartRange(rangeParts[0]);
                setFieldValueEndRange(rangeParts[1]);
            } else if (props.queryItem.filter.values.length === 2) {
                setFieldValueStartRange(props.queryItem.filter.values[0]);
                setFieldValueEndRange(props.queryItem.filter.values[1]);
            }
        } else {
            setFieldValueMultiple(props.queryItem.filter.values.join(","));
        }

    }, [])

    useEffect(() => {
        // Iterate through the whitelistedQueryFields, find the corresponding field from the Datasource fields, and then
        // build a new category object with the subcategory field name and key.  All fields should be grouped together if they have the same category.
        const newWhitelistCategories = props.whitelistedQueryFields.map((field) => {
            const fieldObj = props.dataSource?.fields?.find((f) => f.key === field);
            if (fieldObj) {
                const category = props.dataSource?.categories?.find((c) => c.key === fieldObj.category);
                if (category) {
                    return {
                        key: category.key,
                        name: category.name,
                        subcategories: [{
                            key: fieldObj.key,
                            name: fieldObj.name
                        }]
                    }
                }
            }
            return null;
        }
        ).filter((category) => category !== null) as Category[];
        // Remove duplicates from the subcategories
        const uniqueWhitelistCategories = newWhitelistCategories.reduce((acc: Category[], category: Category) => {
            const existingCategory = acc.find((c) => c.key === category.key);
            if (existingCategory) {
                const existingSubcategoryKeys = existingCategory.subcategories.map((s) => s.key);
                const newSubcategories = category.subcategories.filter((subcategory) => !existingSubcategoryKeys.includes(subcategory.key));
                existingCategory.subcategories.push(...newSubcategories);
            } else {
                acc.push(category);
            }
            return acc;
        }
        , []);
        setCategories(uniqueWhitelistCategories);
    },[props.dataSource, props.whitelistedQueryFields])

    useEffect(() => {
        const filter: QueryFilter = {
            field: fieldName,
            values: createFilterValues(),
            condition: fieldCondition,
            omit: fieldOmit === "true"
        }

        // If the value contains a % wildcard, we need to set the option to 'like'
        if (filter.values.some(v => v.includes("%"))) {
            filter.option = "like";
        }

        props.updateFilter(filter, props.queryItem.id)
    }, [fieldName, fieldValue, fieldCondition, fieldOmit, fieldValueStartRange, fieldValueEndRange, fieldValueMultiple])

    const handleFieldNameChange = (event: SelectChangeEvent) => {
        setFieldName(event.target.value);
    }

    const handleFieldValueChange = (event: SelectChangeEvent<typeof fieldValue>) => {
        const {
            target: { value },
          } = event;
          setFieldValue(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
          );
    }

    const handleFieldConditionChange = (event: SelectChangeEvent) => {
        setFieldCondition(event.target.value);
    }

    const handleFieldOmitChange = (event: SelectChangeEvent) => {
        setFieldOmit(event.target.value);
    }

    const rangeStartValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Remove any punctuation from the value
        const cleanValue = event.target.value.replace(/[,/#!$%^&*;:{}=_`~()]/g, '');

        setFieldValueStartRange(cleanValue);
    }

    const rangeEndValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Remove any punctuation from the value
        const cleanValue = event.target.value.replace(/[,/#!$%^&*;:{}=_`~()]/g, '');

        setFieldValueEndRange(cleanValue);
    }

    const multipleValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValueMultiple(event.target.value);
    }

    const handleCustomFieldChange = (value: string) => {
        console.log("Custom Field Value: ", value)
        setFieldValueMultiple(value);
    }

    const isCustomField = (field: string): boolean => {
        switch (field) {
            case "secondary_state_countycode":
            case "state_countycode":
            case "countycode":
            case "state":
            case "secondary_state":
            case "city_state":
            case "secondary_city_state":
            case "scf":
                return true;
        }
        return false;
    }

    const isRangeField = (field: string): boolean => {
        if (!props.dataSource) {
            return false;
        }

        const fieldObj = props.dataSource.fields?.find(f => f.key === field);

        if (!fieldObj) {
            return false;
        }

        return fieldObj?.range || false;
    }

    const getValues = (field: string): { [key: string]: string } => {
        if (!props.dataSource) {
            return {};
        }

        const fieldObj = props.dataSource.fields?.find(f => f.key === field);

        if (!fieldObj) {
            return {};
        }

        return fieldObj?.values;
    }

    const createFilterValues = (): string[] => {
        if (isRangeField(fieldName)) {
            if (!isNaN(Number(fieldValueStartRange)) && !isNaN(Number(fieldValueEndRange))) {
                return [`${fieldValueStartRange}-${fieldValueEndRange}`];
            } else {
                return [fieldValueStartRange,fieldValueEndRange];
            }
        } else if (Object.entries(getValues(fieldName)).length == 0) {
            const items = typeof fieldValueMultiple === 'string' ? fieldValueMultiple.split(",").map(function(item) {
                return item.trim();
            }) : fieldValueMultiple;
            return items;
        } else {
            return fieldValue;
        }
    }

    const getTooltip = (field: string, moreInfo: string): string => {
        let tip = "";

        switch (field) {
            case "zip4_radius":
                tip = "Expected format: Zip5+Zip4-Radius. Radius is in miles and must be > 0.1. Example: 12345+6789-3";
                break
            case "zip_radius":
                tip = "Expected format: Zip-Radius. Radius is in miles and must be > 0.1. Example: 12345-3";
                break
            case "sale_date":
            case "home_purchase_date":
            case "most_recent_mortgage_date":
            case "move_date":
                tip = "Expected format: YYYY-MM-DD. Example: 2021-12-31";
                break
            case "date_of_name_mmyyyy":
                tip = "Expected format: MMYYYY. Example: 062024";
                break
            case "credit_rating":
                tip = "Credit score compiled and averaged over a Zip+4 region";
                break
        }

        if (moreInfo != "") {
            tip = moreInfo + ". " + tip;
        }

        return tip;
    }

    return (
        <Grid sx={{backgroundColor: props.storefront?.activeColors.cart.cartBackground, ml:-2, mt:-2}} component={Paper}>
            <Stack direction="row" spacing={2} sx={{padding:1}}>
                <FormControl fullWidth size="small" sx={{width:300}}>
                    <InputLabel id="trans-type-label">Field Name</InputLabel>
                    <Select 
                        native
                        labelId="transType" 
                        label="Field Name" 
                        onChange={handleFieldNameChange} 
                        size="small" 
                        value={fieldName} 
                        MenuProps={{
                            sx: {
                                "&& .Mui-selected": {
                                backgroundColor: props.storefront.activeColors.leftMenu.icon
                                }
                            }
                        }}
                    >
                        {categories.map((category) => (
                            <optgroup label={category.name}>
                                {category.subcategories.map((subcategory) => (
                                    <option value={subcategory.key} key={subcategory.key}>{subcategory.name}</option>
                                ))}
                            </optgroup>
                        ))}
                    </Select>
                </FormControl>
                { isCustomField(fieldName)
                    ? <QueryFieldCustom queryItem={props.queryItem} storefront={props.storefront} updateValue={handleCustomFieldChange} />
                    : isRangeField(fieldName) 
                    ? <Stack direction={"row"} spacing={1} alignItems="center">
                        <Tooltip title={getTooltip(fieldName,"")} placement="top">
                            <TextField fullWidth size="small" sx={{width:140}} label="Range Start" variant="outlined" value={fieldValueStartRange} onChange={rangeStartValueChanged} />
                        </Tooltip>
                        <Typography sx={{}}>-</Typography>
                        <Tooltip title={getTooltip(fieldName,"")} placement="top">
                            <TextField fullWidth size="small" sx={{width:140}} label="Range End" variant="outlined" value={fieldValueEndRange} onChange={rangeEndValueChanged} />
                        </Tooltip>
                      </Stack>
                    : Object.entries(getValues(fieldName)).length == 0
                    ? <Tooltip title={getTooltip(fieldName,"Separate multiple values with commas")} placement="top">
                        <TextField fullWidth size="small" sx={{width:300}} label="Values" variant="outlined" value={fieldValueMultiple} onChange={multipleValueChanged} /></Tooltip>
                    : <FormControl fullWidth size="small" sx={{width:300}}>
                    <InputLabel id="values-label">Values</InputLabel>
                        <Select labelId="values" multiple label="Values" onChange={handleFieldValueChange} size="small" value={fieldValue} MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            {Object.entries(getValues(fieldName)).map(([key, value]) => (
                                <MenuItem value={key} key={key}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }

                <Tooltip title="To group items together, specify the same Group number" placement="top">
                    <FormControl fullWidth size="small" sx={{width:120}}>
                        <InputLabel id="condition-label">Condition</InputLabel>
                        <Select labelId="condition" label="Condition" onChange={handleFieldConditionChange} size="small" value={fieldCondition}MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            <MenuItem value="and" key="and">And</MenuItem>
                            <MenuItem value="0" key="0">Group #1</MenuItem>
                            <MenuItem value="1" key="1">Group #2</MenuItem>
                            <MenuItem value="2" key="2">Group #3</MenuItem>
                        </Select>
                    </FormControl>
                </Tooltip>
                <Tooltip title="Select 'true' to remove records matching this field" placement="top">
                    <FormControl fullWidth size="small" sx={{width:100}}>
                        <InputLabel id="trans-type-label">Omit</InputLabel>
                        <Select labelId="transType" label="Omit" onChange={handleFieldOmitChange} size="small" value={fieldOmit}MenuProps={{
                                                    sx: {
                                                    "&& .Mui-selected": {
                                                        backgroundColor: props.storefront.activeColors.leftMenu.icon
                                                    }
                                                    }
                                                }}>
                            <MenuItem value="true" key="true">True</MenuItem>
                            <MenuItem value="false" key="false">False</MenuItem>
                        </Select>
                    </FormControl>
                </Tooltip>
                <IconButton sx={{marginTop:-.5, color:props.storefront.activeColors.cart.cartIconBackground}} size="small" type="button" onClick={() => props.removeField(props.queryItem.id) } >
                    <DeleteForeverIcon />
                </IconButton>
            </Stack>
        </Grid>
    );
};

export default QueryFieldForm;